/**
 * Category Landing Page Component
 * Shows when user navigates to a pure category (no search, no subcategory, no filters)
 * Features: Hero Banner, Multi-Language Subcategory Grid, Skeleton Loaders, Breadcrumbs
 */
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronRight, LayoutGrid } from 'lucide-react';
import { API, useLanguage } from '../App';
import { useIsland } from '../contexts/IslandContext';
import { getSubcategoryIcon } from '../utils/categoryIcons';
import { Breadcrumbs } from './ui/Breadcrumbs';
import { getSubcategoryTranslation, getCategoryTranslation } from '../i18n-subcategories';

// Subcategory sub-labels for enhanced UI (optional descriptions)
const SUBCATEGORY_SUB_LABELS = {
  // Real Estate
  'House': { en: 'Family homes', nl: 'Gezinswoningen', es: 'Casas familiares' },
  'Houses': { en: 'Family homes', nl: 'Gezinswoningen', es: 'Casas familiares' },
  'Villa': { en: 'Luxury living', nl: 'Luxe wonen', es: 'Vida de lujo' },
  'Apartments': { en: 'Urban living', nl: 'Stedelijk wonen', es: 'Vida urbana' },
  'Land': { en: 'Build your dream', nl: 'Bouw je droom', es: 'Construye tu sueño' },
  'Monument': { en: 'Historic properties', nl: 'Historische panden', es: 'Propiedades históricas' },
  'Commercial Property': { en: 'Business spaces', nl: 'Bedrijfsruimtes', es: 'Espacios de negocios' },
  'Property Wanted': { en: 'Looking to buy', nl: 'Op zoek naar', es: 'Buscando comprar' },
  'Rental': { en: 'Rental options', nl: 'Verhuurmogelijkheden', es: 'Opciones de alquiler' },
  
  // Vehicles
  'Cars': { en: 'All makes & models', nl: 'Alle merken', es: 'Todas las marcas' },
  'Motorcycles': { en: 'Two-wheelers', nl: 'Tweewielers', es: 'Dos ruedas' },
  'SUVs / Pickups': { en: 'SUVs & Pickups', nl: 'SUV\'s & Pickups', es: 'SUVs y Pickups' },
  'Vehicle Parts / Accessories': { en: 'Parts & accessories', nl: 'Onderdelen & accessoires', es: 'Piezas y accesorios' },
  'Rental / Leasing': { en: 'Flexible options', nl: 'Flexibele opties', es: 'Opciones flexibles' },
  'Taxis / Transfers': { en: 'Transport services', nl: 'Vervoersdiensten', es: 'Servicios de transporte' },
  
  // Boats & Marine
  'Boats & Yachts': { en: 'Marine vessels', nl: 'Vaartuigen', es: 'Embarcaciones' },
  'Jet Skis & Jetboats': { en: 'High-speed fun', nl: 'Snelle pret', es: 'Diversión a alta velocidad' },
  'Kayaks & Paddles': { en: 'Paddle sports', nl: 'Peddelsport', es: 'Deportes de remo' },
  'Fishing Gear & Boats': { en: 'Fishing equipment', nl: 'Visuitrusting', es: 'Equipo de pesca' },
  'Boat Parts & Accessories': { en: 'Marine parts', nl: 'Bootonderdelen', es: 'Piezas náuticas' },
  
  // Electronics
  'Smartphones': { en: 'Mobile devices', nl: 'Mobiele apparaten', es: 'Dispositivos móviles' },
  'Laptops & Computers': { en: 'Desktop & laptop', nl: 'Desktop & laptop', es: 'Escritorio y portátil' },
  'Tablets': { en: 'Portable computing', nl: 'Draagbaar computing', es: 'Computación portátil' },
  'TV & Audio': { en: 'Entertainment', nl: 'Entertainment', es: 'Entretenimiento' },
  'Printers & Scanners': { en: 'Office equipment', nl: 'Kantoorapparatuur', es: 'Equipos de oficina' },
  
  // Fashion
  "Men's Clothing": { en: "Men's fashion", nl: 'Herenmode', es: 'Moda masculina' },
  "Women's Clothing": { en: "Women's fashion", nl: 'Damesmode', es: 'Moda femenina' },
  'Kids & Baby Clothing': { en: "Children's wear", nl: 'Kinderkleding', es: 'Ropa infantil' },
  'Shoes': { en: 'Footwear', nl: 'Schoeisel', es: 'Calzado' },
  'Bags & Handbags': { en: 'Bags & accessories', nl: 'Tassen & accessoires', es: 'Bolsos y accesorios' },
  'Jewelry': { en: 'Precious items', nl: 'Kostbaarheden', es: 'Artículos preciosos' },
  'Watches': { en: 'Timepieces', nl: 'Horloges', es: 'Relojes' },
  'Sunglasses & Accessories': { en: 'Style accessories', nl: 'Stijlaccessoires', es: 'Accesorios de estilo' },
  'Designer / Luxury Fashion': { en: 'Premium brands', nl: 'Premium merken', es: 'Marcas premium' },
  'Sportswear': { en: 'Athletic wear', nl: 'Sportkleding', es: 'Ropa deportiva' },
  
  // Furniture
  'Living Room': { en: 'Comfort & style', nl: 'Comfort & stijl', es: 'Comodidad y estilo' },
  'Bedroom': { en: 'Rest & relaxation', nl: 'Rust & ontspanning', es: 'Descanso y relajación' },
  'Beds & Mattresses': { en: 'Sleep solutions', nl: 'Slaapoplossingen', es: 'Soluciones para dormir' },
  'Tables & Dining Sets': { en: 'Dining furniture', nl: 'Eetmeubels', es: 'Muebles de comedor' },
  'Chairs': { en: 'Seating options', nl: 'Zitmogelijkheden', es: 'Opciones de asiento' },
  'Sofas & Couches': { en: 'Living room seating', nl: 'Woonkamerzitjes', es: 'Asientos de sala' },
  'Cabinets & Storage': { en: 'Organization', nl: 'Organisatie', es: 'Organización' },
  'Wardrobes & Dressers': { en: 'Clothing storage', nl: 'Kledingopslag', es: 'Almacenamiento de ropa' },
  'Office Furniture': { en: 'Work from home', nl: 'Thuiswerken', es: 'Trabajo desde casa' },
  'Outdoor / Patio Furniture': { en: 'Garden & patio', nl: 'Tuin & terras', es: 'Jardín y patio' },
  'Kids Furniture': { en: "Children's rooms", nl: 'Kinderkamers', es: 'Habitaciones infantiles' },
  
  // Sports & Outdoor
  'Fitness Equipment': { en: 'Stay fit', nl: 'Blijf fit', es: 'Mantente en forma' },
  'Sports Equipment': { en: 'Stay active', nl: 'Blijf actief', es: 'Mantente activo' },
  'Bikes & Cycling': { en: 'Cycling gear', nl: 'Fietsuitrusting', es: 'Equipo de ciclismo' },
  'Surfing, Kite & SUP': { en: 'Catch the wave', nl: 'Pak de golf', es: 'Atrapa la ola' },
  'Diving & Snorkeling': { en: 'Explore underwater', nl: 'Onderwater ontdekken', es: 'Explora bajo el agua' },
  'Camping & Hiking': { en: 'Outdoor adventures', nl: 'Buitenactiviteiten', es: 'Aventuras al aire libre' },
  'Beach & Swimming Gear': { en: 'Beach fun', nl: 'Strandplezier', es: 'Diversión en la playa' },
  'BBQ & Outdoor Cooking': { en: 'Outdoor cooking', nl: 'Buitenkoken', es: 'Cocina al aire libre' },
  'Garden & Yard Equipment': { en: 'Garden tools', nl: 'Tuingereedschap', es: 'Herramientas de jardín' },
  
  // Baby & Kids
  'Toys': { en: 'Play time', nl: 'Speeltijd', es: 'Hora de jugar' },
  'Baby Gear': { en: 'Baby essentials', nl: 'Baby benodigdheden', es: 'Artículos esenciales' },
  'Strollers': { en: 'On the go', nl: 'Onderweg', es: 'En movimiento' },
  
  // Pets
  'Dogs': { en: 'Dogs', nl: 'Honden', es: 'Perros' },
  'Cats': { en: 'Cats', nl: 'Katten', es: 'Gatos' },
  'Pets for Sale': { en: 'Find a friend', nl: 'Vind een vriend', es: 'Encuentra un amigo' },
  'Pet Supplies': { en: 'Pet essentials', nl: 'Dierbenodigdheden', es: 'Artículos para mascotas' },
  'Pet Services': { en: 'Pet care', nl: 'Dierenverzorging', es: 'Cuidado de mascotas' },
  'Pets Rescue': { en: 'Adopt a pet', nl: 'Adopteer', es: 'Adopta' },
  
  // Beauty & Health
  'Skincare': { en: 'Skin health', nl: 'Huidgezondheid', es: 'Salud de la piel' },
  'Hair Products': { en: 'Hair care', nl: 'Haarverzorging', es: 'Cuidado del cabello' },
  'Beauty Products': { en: 'Beauty essentials', nl: 'Beauty benodigdheden', es: 'Esenciales de belleza' },
  'Beauty & Wellness': { en: 'Self care', nl: 'Zelfzorg', es: 'Autocuidado' },
  'Treatments': { en: 'Professional care', nl: 'Professionele zorg', es: 'Cuidado profesional' },
  
  // Business & Industrial
  'Business Equipment': { en: 'Commercial tools', nl: 'Commerciële tools', es: 'Herramientas comerciales' },
  'Restaurant Equipment': { en: 'Hospitality', nl: 'Horeca', es: 'Hostelería' },
  'Heavy Machinery': { en: 'Industrial', nl: 'Industrieel', es: 'Industrial' },
  'Industrial Tools': { en: 'Professional tools', nl: 'Professioneel gereedschap', es: 'Herramientas profesionales' },
  'Solar & Energy': { en: 'Renewable energy', nl: 'Duurzame energie', es: 'Energía renovable' },
  'Security': { en: 'Security systems', nl: 'Beveiligingssystemen', es: 'Sistemas de seguridad' },
  
  // Services
  'Home Services (cleaning, repairs, plumbing)': { en: 'Home maintenance', nl: 'Huisonderhoud', es: 'Mantenimiento del hogar' },
  'Construction & Renovation': { en: 'Building services', nl: 'Bouwdiensten', es: 'Servicios de construcción' },
  'Moving & Transport': { en: 'Relocation', nl: 'Verhuizing', es: 'Mudanza' },
  'Event Services': { en: 'Celebrations', nl: 'Vieringen', es: 'Celebraciones' },
  'Photography & Video': { en: 'Capture moments', nl: 'Leg momenten vast', es: 'Captura momentos' },
  'Business Services': { en: 'Professional help', nl: 'Professionele hulp', es: 'Ayuda profesional' },
  'Freelance Services': { en: 'Independent work', nl: 'Zelfstandig werk', es: 'Trabajo independiente' },
  'IT & Tech Services': { en: 'Tech support', nl: 'Tech ondersteuning', es: 'Soporte técnico' },
  'Marketing & Design': { en: 'Creative services', nl: 'Creatieve diensten', es: 'Servicios creativos' },
  'Lessons & Tutoring': { en: 'Learn & grow', nl: 'Leren & groeien', es: 'Aprende y crece' },
  'Professional': { en: 'Business services', nl: 'Zakelijke diensten', es: 'Servicios empresariales' },
  
  // Jobs
  'Full-time': { en: 'Career opportunities', nl: 'Carrièremogelijkheden', es: 'Oportunidades de carrera' },
  'Part-time': { en: 'Flexible hours', nl: 'Flexibele uren', es: 'Horario flexible' },
  'Freelance': { en: 'Be your own boss', nl: 'Wees je eigen baas', es: 'Sé tu propio jefe' },
  'Internships': { en: 'Start your career', nl: 'Start je carrière', es: 'Inicia tu carrera' },
  'Remote Jobs': { en: 'Work from anywhere', nl: 'Werk overal', es: 'Trabaja desde cualquier lugar' },
  'Student Jobs': { en: 'Student opportunities', nl: 'Studentenbanen', es: 'Trabajos para estudiantes' },
  'Hospitality & Tourism': { en: 'Tourism sector', nl: 'Toerismesector', es: 'Sector turístico' },
  
  // Tours & Experiences
  'Island Tours': { en: 'Explore the island', nl: 'Ontdek het eiland', es: 'Explora la isla' },
  'Boat Tours': { en: 'Marine adventures', nl: 'Zeeavonturen', es: 'Aventuras marinas' },
  'Diving Tours': { en: 'Underwater world', nl: 'Onderwaterwereld', es: 'Mundo submarino' },
  'Nature & Wildlife Tours': { en: 'Nature exploration', nl: 'Natuurverkenning', es: 'Exploración de la naturaleza' },
  
  // Community
  'Lost & Found': { en: 'Help find items', nl: 'Help zoeken', es: 'Ayuda a encontrar' },
  'Free Items': { en: 'Free stuff', nl: 'Gratis spullen', es: 'Cosas gratis' },
  'Donations': { en: 'Give back', nl: 'Geef terug', es: 'Devolver' },
  'Barter / Trade': { en: 'Trade items', nl: 'Ruil spullen', es: 'Intercambia artículos' },
  
  // Default
  'Other': { en: 'More options', nl: 'Meer opties', es: 'Más opciones' },
};

// Get subcategory sub-label
const getSubLabel = (subcategory, lang = 'en') => {
  const subLabel = SUBCATEGORY_SUB_LABELS[subcategory];
  if (subLabel) {
    return subLabel[lang] || subLabel.en || '';
  }
  return '';
};

// Get translated subcategory name and sub-label using centralized translations
const getTranslation = (subcategory, lang = 'en') => {
  return {
    name: getSubcategoryTranslation(subcategory, lang),
    subLabel: getSubLabel(subcategory, lang)
  };
};

// Category Hero Banner Component (Sponsored/Ad Banner)
const CategoryHeroBanner = ({ category }) => {
  const [banner, setBanner] = useState(null);
  const [loading, setLoading] = useState(true);
  const { currentIsland } = useIsland();

  useEffect(() => {
    const abortController = new AbortController();
    
    // Reset state immediately when category changes to prevent stale ads
    setBanner(null);
    setLoading(true);

    const fetchBanner = async () => {
      try {
        const params = new URLSearchParams();
        params.append('position', 'category_landing');
        params.append('category', category.id);
        params.append('limit', '1');
        if (currentIsland) {
          params.append('island', currentIsland);
        }
        
        const response = await fetch(`${API}/banners?${params}`, {
          signal: abortController.signal
        });
        if (response.ok) {
          const data = await response.json();
          if (data.length > 0 && data[0].active) {
            setBanner(data[0]);
          }
        }
      } catch (error) {
        // Ignore aborted requests (user navigated away)
        if (error.name === 'AbortError') return;
        console.error('Error fetching category banner:', error);
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchBanner();
    
    // Cleanup: abort pending request when category changes or component unmounts
    return () => abortController.abort();
  }, [category.id, currentIsland]);

  // Show skeleton while loading to prevent layout shift
  if (loading) {
    return (
      <div className="relative w-full overflow-hidden rounded-xl mb-4 shadow-lg" data-testid="category-hero-banner-skeleton">
        <div className="aspect-[6/1] bg-secondary/30 animate-pulse" />
      </div>
    );
  }

  // Don't render anything if no banner
  if (!banner) return null;

  return (
    <a
      href={banner.link_url || '#'}
      target={banner.link_url ? '_blank' : undefined}
      rel={banner.link_url ? 'noopener noreferrer' : undefined}
      className="block relative w-full overflow-hidden rounded-xl mb-4 group shadow-lg"
      data-testid="category-hero-banner"
    >
      <div className="aspect-[6/1] relative">
        <img
          src={banner.image_url}
          alt={banner.title || 'Category Banner'}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/20 to-transparent" />
        
        {/* Banner Content */}
        <div className="absolute inset-0 flex items-center p-3 md:p-5">
          <div className="max-w-md">
            {banner.title && (
              <h3 className="text-sm md:text-lg font-bold text-white mb-0.5 drop-shadow-lg">
                {banner.title}
              </h3>
            )}
            {banner.description && (
              <p className="text-[10px] md:text-xs text-white/90 line-clamp-1">
                {banner.description}
              </p>
            )}
          </div>
        </div>
      </div>
    </a>
  );
};

// CategoryBreadcrumbs - Uses reusable Breadcrumbs component with fixed-height wrapper
const CategoryBreadcrumbs = ({ category }) => {
  const items = [
    { label: 'Home', href: '/' },
    { label: 'Categories', href: '/categories' },
    { label: category.name } // Last item - no href, shown as span
  ];
  
  return (
    <div className="flex h-10 items-center mb-4" data-testid="category-breadcrumbs-wrapper">
      <Breadcrumbs 
        items={items}
        showBackButton={true}
        data-testid="category-breadcrumbs"
      />
    </div>
  );
};

// Skeleton Loader for Subcategory Card - Premium Card Design
const SubcategoryCardSkeleton = () => (
  <div className="bg-white dark:bg-zinc-900/40 border border-border rounded-3xl overflow-hidden flex flex-col shadow-md dark:shadow-none dark:backdrop-blur-md">
    {/* Icon skeleton */}
    <div className="aspect-[4/3] bg-secondary/50 dark:bg-secondary/20 flex items-center justify-center">
      <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-primary/10 dark:bg-white/20 animate-pulse" />
    </div>
    {/* Content skeleton */}
    <div className="p-4 sm:p-5">
      <div className="h-4 w-24 bg-muted rounded-lg mb-2 animate-pulse" />
      <div className="h-3 w-16 bg-muted/70 rounded-lg animate-pulse" />
    </div>
  </div>
);

// Premium Subcategory Card Component - Modern app-like design
const SubcategoryCard = ({ subcategory, subcategoryName, onClick, language = 'en', listingCount = null }) => {
  const Icon = getSubcategoryIcon(subcategory);
  // Use subcategoryName if provided (object format), otherwise use subcategory (string format)
  const nameToTranslate = subcategoryName || subcategory;
  const { name: translatedName, subLabel } = getTranslation(nameToTranslate, language);
  
  return (
    <button
      onClick={() => onClick(subcategory)}
      className="
        relative bg-white dark:bg-zinc-900/40 
        border border-border 
        overflow-hidden 
        group cursor-pointer 
        text-left
        shadow-sm dark:shadow-none
        dark:backdrop-blur-md
        transition-all duration-300 ease-out
        hover:border-primary/40
        hover:shadow-lg
        
        /* Mobile: List layout */
        flex flex-row items-center gap-3 p-3 rounded-xl
        
        /* Desktop: Card layout */
        md:flex-col md:p-0 md:rounded-3xl md:shadow-md
      "
      data-testid={`subcategory-card-${subcategory.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {/* Icon - Mobile: small circle, Desktop: large container */}
      <div className="
        relative flex items-center justify-center
        
        /* Mobile: Compact icon */
        w-12 h-12 rounded-xl bg-primary/10 dark:bg-white/10 flex-shrink-0
        
        /* Desktop: Full icon container */
        md:w-full md:h-auto md:aspect-[4/3] md:rounded-none md:bg-secondary/50 md:dark:bg-secondary/20
      ">
        <div className="
          relative
          flex items-center justify-center
          
          /* Mobile: Icon only */
          w-full h-full
          
          /* Desktop: Icon box with effects */
          md:w-16 md:h-16 lg:w-20 lg:h-20 
          md:rounded-2xl 
          md:bg-primary/10 md:dark:bg-white/20 
          md:backdrop-blur-md
          md:border md:border-primary/20 md:dark:border-white/30
          md:shadow-lg
          md:transition-all md:duration-300
          md:group-hover:scale-110 md:group-hover:-translate-y-2
        " style={{ transitionTimingFunction: 'cubic-bezier(0.34, 1.56, 0.64, 1)' }}>
          <Icon className="
            w-6 h-6 
            md:w-8 md:h-8 lg:w-10 lg:h-10 
            text-primary dark:text-white 
            dark:drop-shadow-md
          " />
          
          {/* Listing Count Badge - Desktop only, inside icon box */}
          {listingCount !== null && listingCount > 0 && (
            <div className="
              absolute -top-1.5 -right-1.5
              hidden md:flex
              z-10 
              px-2 py-0.5 
              bg-primary text-primary-foreground 
              text-[10px] font-bold
              rounded-full 
              ring-2 ring-white dark:ring-zinc-900
              shadow-sm
            ">
              {listingCount > 999 ? '999+' : listingCount}
            </div>
          )}
        </div>
      </div>
      
      {/* Content - Mobile: inline, Desktop: below */}
      <div className="
        flex flex-col gap-0.5
        flex-1 min-w-0
        
        /* Desktop: padding */
        md:p-4 lg:p-5
      ">
        {/* Title - Mobile: no truncate, Desktop: truncate */}
        <span className="text-foreground font-semibold text-sm md:text-base tracking-tight md:truncate">
          {translatedName}
        </span>
        
        {/* Subtitle */}
        {subLabel && (
          <span className="text-muted-foreground text-[11px] font-medium uppercase tracking-wider opacity-70 hidden md:block">
            {subLabel}
          </span>
        )}
        
        {/* Mobile: Show count inline */}
        {listingCount !== null && listingCount > 0 && (
          <span className="text-muted-foreground text-xs md:hidden">
            {listingCount} {listingCount === 1 ? 'listing' : 'listings'}
          </span>
        )}
      </div>
      
      {/* Mobile: Chevron */}
      <ChevronRight className="w-5 h-5 text-muted-foreground md:hidden flex-shrink-0" />
    </button>
  );
};

// Show All Card Component - First card in the grid
const ShowAllCard = ({ categoryName, onClick, language = 'en', totalCount = null }) => {
  // Multi-language support for "Show All" card
  const translations = {
    title: { en: `All ${categoryName}`, nl: `Alle ${categoryName}`, es: `Todo ${categoryName}` },
    subtitle: { en: 'Browse everything', nl: 'Bekijk alles', es: 'Ver todo' }
  };
  
  const title = translations.title[language] || translations.title.en;
  const subtitle = translations.subtitle[language] || translations.subtitle.en;
  
  return (
    <button
      onClick={onClick}
      className="
        relative bg-primary/5 dark:bg-primary/10
        border border-primary/20 dark:border-primary/30
        overflow-hidden 
        group cursor-pointer 
        text-left
        shadow-sm dark:shadow-none
        dark:backdrop-blur-md
        transition-all duration-300 ease-out
        hover:border-primary/40
        hover:shadow-lg
        hover:bg-primary/10 dark:hover:bg-primary/15
        
        /* Mobile: List layout */
        flex flex-row items-center gap-3 p-3 rounded-xl
        
        /* Desktop: Card layout */
        md:flex-col md:p-0 md:rounded-3xl md:shadow-md
      "
      data-testid="show-all-card"
    >
      {/* Icon - Mobile: small, Desktop: large container */}
      <div className="
        relative flex items-center justify-center
        
        /* Mobile: Compact icon */
        w-12 h-12 rounded-xl bg-primary/10 dark:bg-primary/20 flex-shrink-0
        
        /* Desktop: Full icon container */
        md:w-full md:h-auto md:aspect-[4/3] md:rounded-none md:bg-secondary/50 md:dark:bg-secondary/20
      ">
        <div className="
          relative
          flex items-center justify-center
          
          /* Mobile: Icon only */
          w-full h-full
          
          /* Desktop: Icon box with effects */
          md:w-16 md:h-16 lg:w-20 lg:h-20 
          md:rounded-2xl 
          md:bg-primary/10 md:dark:bg-white/20
          md:backdrop-blur-md
          md:border md:border-primary/20 md:dark:border-white/30
          md:shadow-lg
          md:transition-all md:duration-300
          md:group-hover:scale-110 md:group-hover:-translate-y-2
        " style={{ transitionTimingFunction: 'cubic-bezier(0.34, 1.56, 0.64, 1)' }}>
          <LayoutGrid className="
            w-6 h-6 
            md:w-8 md:h-8 lg:w-10 lg:h-10 
            text-primary dark:text-white 
            dark:drop-shadow-md
          " />
          
          {/* Total Count Badge - Desktop only, inside icon box */}
          {totalCount !== null && totalCount > 0 && (
            <div className="
              absolute -top-1.5 -right-1.5
              hidden md:flex
              z-10 
              px-2 py-0.5 
              bg-primary text-primary-foreground 
              text-[10px] font-bold
              rounded-full 
              ring-2 ring-white dark:ring-zinc-900
              shadow-sm
            ">
              {totalCount > 999 ? '999+' : totalCount}
            </div>
          )}
        </div>
      </div>
      
      {/* Content - Mobile: inline, Desktop: below */}
      <div className="
        flex flex-col gap-0.5
        flex-1 min-w-0
        
        /* Desktop: padding */
        md:p-4 lg:p-5
      ">
        {/* Title */}
        <span className="text-foreground font-semibold text-sm md:text-base tracking-tight md:truncate">
          {title}
        </span>
        
        {/* Subtitle - Desktop only */}
        <span className="text-muted-foreground text-[11px] font-medium uppercase tracking-wider opacity-70 hidden md:block">
          {subtitle}
        </span>
        
        {/* Mobile: Show count inline */}
        {totalCount !== null && totalCount > 0 && (
          <span className="text-muted-foreground text-xs md:hidden">
            {totalCount} {totalCount === 1 ? 'listing' : 'listings'}
          </span>
        )}
      </div>
      
      {/* Mobile: Chevron */}
      <ChevronRight className="w-5 h-5 text-muted-foreground md:hidden flex-shrink-0" />
    </button>
  );
};

// Main Category Landing Component
const CategoryLanding = ({ category, onNavigateToSubcategory, onBack }) => {
  const navigate = useNavigate();
  const { language } = useLanguage(); // Use language context for cross-tab sync
  const [isLoadingGrid, setIsLoadingGrid] = useState(true);
  const [listingCounts, setListingCounts] = useState({});
  const { currentIsland } = useIsland();

  useEffect(() => {
    // Simulate initial grid loading
    const timer = setTimeout(() => setIsLoadingGrid(false), 300);
    return () => clearTimeout(timer);
  }, []);

  // Fetch listing counts for each subcategory
  useEffect(() => {
    const fetchListingCounts = async () => {
      if (!category?.subcategories?.length) return;
      
      try {
        const params = new URLSearchParams();
        params.append('category', category.id);
        if (currentIsland) {
          params.append('island', currentIsland);
        }
        
        // Fetch counts for all subcategories
        const response = await fetch(`${API}/listings/counts-by-subcategory?${params}`);
        if (response.ok) {
          const data = await response.json();
          setListingCounts(data);
        }
      } catch (error) {
        console.error('Error fetching listing counts:', error);
      }
    };

    fetchListingCounts();
  }, [category, currentIsland]);

  const handleSubcategoryClick = (subcategory) => {
    // Scroll to top before navigating
    window.scrollTo({ top: 0, behavior: 'instant' });
    onNavigateToSubcategory(subcategory);
  };

  const subcategories = category.subcategories || [];
  
  // Calculate total listings across all subcategories
  const totalListings = Object.values(listingCounts).reduce((sum, count) => sum + (count || 0), 0);

  // Handle "Show All" click - navigate to marketplace with only category filter
  const handleShowAllClick = () => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    onNavigateToSubcategory(''); // Empty string = no subcategory filter
  };

  return (
    <div data-testid="category-landing-page">
      {/* Breadcrumbs - Fixed height wrapper prevents layout shift */}
      <CategoryBreadcrumbs category={category} />

      {/* Category Hero Banner - Sponsored Ad (Full Width with Shadow) */}
      <CategoryHeroBanner category={category} />

      {/* Section Heading - Translated */}
      <h2 className="text-xl font-bold text-foreground mt-6 mb-4">
        {language === 'nl' ? `Bekijk ${getCategoryTranslation(category.name, language)}` 
         : language === 'es' ? `Explorar ${getCategoryTranslation(category.name, language)}`
         : `Browse ${getCategoryTranslation(category.name, 'en')}`}
      </h2>

      {/* Subcategory List (Mobile) / Grid (Desktop) */}
      {subcategories.length > 0 && (
        <div className="flex flex-col gap-2 md:grid md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 md:gap-4">
          {isLoadingGrid ? (
            // Skeleton loaders while grid initializes (including one for Show All)
            Array.from({ length: Math.min(subcategories.length + 1, 11) }).map((_, i) => (
              <SubcategoryCardSkeleton key={`skeleton-${i}`} />
            ))
          ) : (
            <>
              {/* Show All Card - Always first */}
              <ShowAllCard
                categoryName={getCategoryTranslation(category.name, language)}
                onClick={handleShowAllClick}
                language={language}
                totalCount={totalListings || null}
              />
              {/* Subcategory Cards */}
              {subcategories.map((subcategory) => {
                const subId = typeof subcategory === 'object' ? subcategory.id : subcategory;
                const subName = typeof subcategory === 'object' ? subcategory.name : subcategory;
                return (
                <SubcategoryCard
                  key={subId}
                  subcategory={subId}
                  subcategoryName={subName}
                  onClick={handleSubcategoryClick}
                  language={language}
                  listingCount={listingCounts[subId] || null}
                />
              );})}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default CategoryLanding;
